import React, { useContext, useMemo } from "react";
import { HashLink as Link } from "react-router-hash-link";
import { isNil } from "ramda";
import { styled } from "styled-components";

import { AffinityFormContext } from "affinityForm";
import { ExecuteButton } from "affinityForm/components/executeButton";
import { Summary } from "affinityUI/data/summary";
import { ButtonVariants, NavigateButton } from "affinityUI/elements/button";
import { Card } from "affinityUI/elements/card";
import { VisuallyHidden } from "affinityUI/elements/visuallyHidden";
import { ButtonGroup } from "affinityUI/form/components";

import type { PermissionGroupRecord, RoleFormData } from "../types";

const PermissionsUL = styled.ul`
  margin: 0;
  padding: 0;
`;

export const RoleConfirmation: React.FC<{
  permissionGroups: PermissionGroupRecord[];
}> = ({ permissionGroups }) => {
  const formContext = useContext(AffinityFormContext);
  const roleData = formContext?.formData as Maybe<RoleFormData>;

  const permissionNames = useMemo(
    () => Object.fromEntries(permissionGroups.map(({ id, description }) => [id, description])),
    [permissionGroups]
  );

  if (isNil(roleData)) {
    return <>Error...</>;
  }

  return (
    <Card>
      <Summary.Section
        title="Role"
        actions={
          <Link to="..#role">
            Edit <VisuallyHidden>Role</VisuallyHidden>
          </Link>
        }
      >
        <Summary.List>
          <Summary.Item label="Role name">{roleData.name}</Summary.Item>
          <Summary.Item label="Description">{roleData.description}</Summary.Item>
        </Summary.List>
      </Summary.Section>
      <Summary.Section
        title="User permissions"
        actions={
          <Link to="..#user-permissions">
            Edit <VisuallyHidden>User permissions</VisuallyHidden>
          </Link>
        }
      >
        <Summary.List>
          <Summary.Item label="User actions">
            <PermissionsUL>
              {roleData.permissionGroups?.map((permissionGroup) => (
                <li key={permissionGroup}>{permissionNames[permissionGroup]}</li>
              ))}
            </PermissionsUL>
          </Summary.Item>
        </Summary.List>
      </Summary.Section>
      <ButtonGroup>
        <NavigateButton to=".." variant={ButtonVariants.SECONDARY}>
          Cancel
        </NavigateButton>
        <ExecuteButton>Confirm and save</ExecuteButton>
      </ButtonGroup>
    </Card>
  );
};
